import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { apiRootSelector } from '@schir-int-client/api-root';
import { verfahrenSingleSelector } from 'libs/verfahren-shared/src/lib/verfahren.selectors';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import {
	setKontaktAssignmentAction,
	setKontaktAssignmentSuccessAction,
	createKontaktAction,
	createKontaktSuccessAction,
	getSearchKontakteOwner,
	loadKontakteByVerfahrenAction,
	loadKontakteByVerfahrenSuccessAction,
	searchKontakteAction,
	searchKontakteSuccessAction,
	updateKontaktAction,
	updateKontaktSuccessAction,
	updateVerfahrenKontaktFreiTextAction,
	updateVerfahrenKontaktFreiTextSuccessAction,
} from './adressverwaltung.actions';
import { AdressverwaltungFacade } from './adressverwaltung.facade';
import { AdressverwaltungService } from './adressverwaltung.service';
import { AppState } from '../../../../apps/int-client/src/app/shared/store/app-state.model';

@Injectable()
export class AdressverwaltungEffects {
	constructor(
		private actions: Actions,
		private adressverwaltungService: AdressverwaltungService,
		private adressverwaltungFacade: AdressverwaltungFacade,
		private store: Store<AppState>
	) {}

	searchKontakte = createEffect(() =>
		this.actions.pipe(
			ofType(searchKontakteAction),
			withLatestFrom(this.store.select(apiRootSelector)),
			switchMap(([{ condition, filter, limit }, apiRoot]) => {
				return this.adressverwaltungService.search(apiRoot, condition, filter, limit).pipe(
					map(kontakte =>
						searchKontakteSuccessAction({
							owner: getSearchKontakteOwner(condition, filter, limit),
							kontakte,
						})
					)
				);
			})
		)
	);

	loadKontakteInVerfahren = createEffect(() =>
		this.actions.pipe(
			ofType(loadKontakteByVerfahrenAction),
			switchMap(({ verfahren }) => {
				return this.adressverwaltungService
					.loadKontakteInVerfahren(verfahren)
					.pipe(map(kontakte => loadKontakteByVerfahrenSuccessAction({ kontakte })));
			})
		)
	);

	createKontakt = createEffect(() =>
		this.actions.pipe(
			ofType(createKontaktAction),
			withLatestFrom(this.store.select(apiRootSelector)),
			switchMap(([{ kontakt }, apiRoot]) => {
				return this.adressverwaltungService
					.create(apiRoot, kontakt)
					.pipe(map(kontakt => createKontaktSuccessAction({ kontakt })));
			})
		)
	);

	assignKontakt = createEffect(() =>
		this.actions.pipe(
			ofType(setKontaktAssignmentAction),
			withLatestFrom(this.store.select(verfahrenSingleSelector)),
			switchMap(([{ verfahrenKontakt }, verfahren]) => {
				return this.adressverwaltungService
					.setKontaktAssignment(verfahren, verfahrenKontakt)
					.pipe(map(verfahren => setKontaktAssignmentSuccessAction({ verfahren })));
			})
		)
	);

	updateKontakt = createEffect(() =>
		this.actions.pipe(
			ofType(updateKontaktAction),
			withLatestFrom(this.adressverwaltungFacade.selectedKontakt),
			switchMap(([{ kontakt }, kontaktExt]) => {
				return this.adressverwaltungService
					.update(kontaktExt, kontakt)
					.pipe(map(kontakt => updateKontaktSuccessAction({ kontakt })));
			})
		)
	);

	updateVerfahrenKontaktFreiText = createEffect(() =>
		this.actions.pipe(
			ofType(updateVerfahrenKontaktFreiTextAction),
			switchMap(({ verfahrenKontakt }) => {
				return this.adressverwaltungService
					.updateVerfahrenKontaktFreiText(verfahrenKontakt)
					.pipe(map(updated => updateVerfahrenKontaktFreiTextSuccessAction({ verfahrenKontakt: updated })));
			})
		)
	);
}
